import Button from '@mui/material/Button';
import Check from '@mui/icons-material/Check';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MediaSourceSummary } from '../../api/types';
import { describeVersion, fileName } from './versionText';

interface VersionPickerProps {
  versions: MediaSourceSummary[];
  value: string | undefined;
  onChange: (mediaSourceId: string) => void;
}

/// Which copy of this to play. One file is the ordinary case and says nothing; several are a
/// choice somebody has to be given, because the difference between them - a disc rip and
/// something that fits down a phone line - is the difference between watching it and waiting.
export default function VersionPicker({ versions, value, onChange }: VersionPickerProps) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement>();

  if (versions.length < 2) {
    return null;
  }

  const chosen = versions.find((version) => version.id === value) ?? versions[0];

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<LayersOutlined />}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        {describeVersion(chosen, t, { withSize: false })}
      </Button>

      <Menu anchorEl={anchor} open={anchor !== undefined} onClose={() => setAnchor(undefined)}>
        {versions.map((version) => (
          <MenuItem
            key={version.id}
            selected={version.id === chosen.id}
            onClick={() => {
              onChange(version.id);
              setAnchor(undefined);
            }}
          >
            <ListItemIcon>{version.id === chosen.id && <Check fontSize="small" />}</ListItemIcon>
            <ListItemText primary={describeVersion(version, t, { withSize: false })} secondary={fileName(version.path)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
